// Live class data for the site's own schedule (owner direction, Sept 30). It reads the same public
// feed the scheduling provider's calendar uses, so times, rooms, instructors and spots always match
// the official schedule. If the feed can't be read, the schedule falls back to the provider's own
// calendar.

export const SCHEDULE_FEED = 'https://agency-common-ui.pages.dev/ca_deltavac/groupex/calendar';
export const CLUB_TIME_ZONE = 'America/Los_Angeles';
export const CLUB_ADDRESS = 'Delta Valley Health Club, 120 Guthrie Ln, Brentwood, CA 94513';
const FEED_TIMEOUT_MS = 12000;

// Class groups follow the site's own schedule pages; colors follow each program's haze.
export const CLASS_GROUPS = [
  { id: 'cardio', label: 'Cardio', rgb: '56 108 204' },
  { id: 'strength', label: 'Strength', rgb: '52 140 92' },
  { id: 'mind-body', label: 'Mind-Body', rgb: '146 116 208' },
  { id: 'reformer', label: 'Reformer Pilates', rgb: '104 64 184' },
  { id: 'mx4', label: 'MX4', rgb: '158 26 48' },
  { id: 'class', label: 'Group Class', rgb: '45 111 145' },
];
export const GROUP_BY_ID = Object.fromEntries(CLASS_GROUPS.map((group) => [group.id, group]));

export const TIMES_OF_DAY = [
  { id: 'morning', label: 'Morning', from: 0, to: 12 * 60 },
  { id: 'afternoon', label: 'Afternoon', from: 12 * 60, to: 17 * 60 },
  { id: 'evening', label: 'Evening', from: 17 * 60, to: 24 * 60 },
];

// Court and room bookings share the feed with classes; they aren't classes, so they stay off.
const NOT_A_CLASS = /rent|court|pickle ?ball|cornhole/i;

export function groupOf(name, categories = []) {
  if (NOT_A_CLASS.test(name)) return null;
  if (/^mx4\b/i.test(name) || categories.includes(5)) return 'mx4';
  if (/reformer/i.test(name) || categories.includes(10)) return 'reformer';
  if (/yoga|barre|stretch|pilates|aerobics|fusion/i.test(name)) return 'mind-body';
  if (/spin|zumba|kick|saddle|zone|pedal|cycl|cardio/i.test(name)) return 'cardio';
  if (/blast|f\.i\.t|muscle|overall body|strength|pump|sculpt/i.test(name)) return 'strength';
  return categories.includes(9) ? 'class' : null;
}

// "MX4 ($) 5:30 AM" → "MX4"; "Pilates Reformer ($) 3:30 PM (Intermediate)" → "Reformer Pilates" + level.
export function cleanName(raw) {
  let name = String(raw || '').replace(/\(\$\)/g, '').replace(/\*/g, '');
  let level = null;
  name = name.replace(/\((Intermediate|Beginner|Advanced|All Levels)\)/gi, (_, found) => { level = found; return ''; });
  name = name.replace(/\((AM|PM)\)/gi, '')
    .replace(/\s*-?\s*\d{1,2}:\d{2}\s*(AM|PM)\b/gi, '')
    .replace(/\s*-?\s*\b(AM|PM)\b/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*-\s*$/, '')
    .trim();
  if (/^pilates reformer$/i.test(name)) name = 'Reformer Pilates';
  return { name, level };
}

export function toMinutes(label) {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(label || '').trim());
  if (!match) return null;
  const hours = Number(match[1]) % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0);
  return hours * 60 + Number(match[2]);
}

const money = (value) => Number(String(value || '').replace(/[^0-9.]/g, '')) || 0;
const plain = (text) => String(text || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();

export function normalizeClass(item, iso) {
  const group = groupOf(item.className, item.category || []);
  const start = toMinutes(item.startTime);
  const end = toMinutes(item.endTime);
  if (!group || start === null) return null;
  const { name, level } = cleanName(item.className);
  const spots = /^\d+$/.test(String(item.availableSpots)) ? Number(item.availableSpots) : null;
  return {
    id: `${item.classId}-${iso}-${start}`,
    date: iso,
    name,
    level,
    group,
    start,
    end: end ?? start,
    startLabel: item.startTime,
    endLabel: item.endTime,
    room: item.resource || '',
    location: item.locationName || 'Brentwood',
    instructors: (item.instructors || []).map((person) => person?.name).filter(Boolean),
    spots,
    bookable: item.classStatus === 'AVAILABLE',
    extraFee: /\(\$\)/.test(item.className) || money(item.memberFee) > 0,
    description: plain(item.description),
  };
}

export async function fetchDay(iso, { signal } = {}) {
  const response = await fetch(`${SCHEDULE_FEED}/classes?date=${iso}`, { signal, credentials: 'omit' });
  if (!response.ok) throw new Error(`Schedule feed ${response.status}`);
  const body = await response.json();
  if (!body || !Array.isArray(body.data)) throw new Error('Schedule feed format');
  return body.data.map((item) => normalizeClass(item, iso)).filter(Boolean).sort((a, b) => a.start - b.start || a.name.localeCompare(b.name));
}

const weekCache = new Map();

// All seven days of a week, fetched together; one retry-able promise per week.
export function fetchWeek(days) {
  const key = days[0];
  if (!weekCache.has(key)) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FEED_TIMEOUT_MS);
    const request = Promise.all(days.map((iso) => fetchDay(iso, { signal: controller.signal })))
      .then((lists) => Object.fromEntries(days.map((iso, index) => [iso, lists[index]])))
      .catch((error) => { weekCache.delete(key); throw error; })
      .finally(() => clearTimeout(timer));
    weekCache.set(key, request);
  }
  return weekCache.get(key);
}
export const clearScheduleCache = () => weekCache.clear();

// Dates are handled as club-local "YYYY-MM-DD" strings; arithmetic happens in UTC to avoid DST drift.
const pad = (value) => String(value).padStart(2, '0');
export const isoOf = (date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
export const dateOf = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
export const addDays = (iso, count) => { const date = dateOf(iso); date.setUTCDate(date.getUTCDate() + count); return isoOf(date); };
export const weekOf = (iso) => { const start = addDays(iso, -dateOf(iso).getUTCDay()); return Array.from({ length: 7 }, (_, i) => addDays(start, i)); };

function clubParts(date) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: CLUB_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).map(({ type, value }) => [type, value]));
  return { iso: `${parts.year}-${parts.month}-${parts.day}`, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}
export const clubNow = (date = new Date()) => clubParts(date);

// The UTC instant of a club-local date and time (for calendar files).
export function clubToUtc(iso, minutes) {
  const [y, m, d] = iso.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60);
  const seen = clubParts(new Date(guess));
  const seenUtc = Date.UTC(...seen.iso.split('-').map((part, i) => Number(part) - (i === 1 ? 1 : 0)), Math.floor(seen.minutes / 60), seen.minutes % 60);
  return new Date(guess - (seenUtc - guess));
}

export function liveStatus(item, now) {
  if (item.date !== now.iso) return null;
  if (now.minutes >= item.end) return 'ended';
  if (now.minutes >= item.start) return 'now';
  if (item.start - now.minutes <= 60) return 'soon';
  return null;
}

export const dayLabel = (iso, options) => dateOf(iso).toLocaleDateString('en-US', { timeZone: 'UTC', ...options });

const icsText = (text) => String(text).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
const icsTime = (date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

export function calendarFile(item) {
  const detail = [item.instructors.length && `With ${item.instructors.join(', ')}`, item.room].filter(Boolean).join(' · ');
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Delta Valley Health Club//Class Schedule//EN', 'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${item.id}@deltavalleyhealthclub.com`,
    `DTSTAMP:${icsTime(new Date())}`,
    `DTSTART:${icsTime(clubToUtc(item.date, item.start))}`,
    `DTEND:${icsTime(clubToUtc(item.date, item.end))}`,
    `SUMMARY:${icsText(`${item.name} at Delta Valley`)}`,
    `LOCATION:${icsText(CLUB_ADDRESS)}`,
    ...(detail ? [`DESCRIPTION:${icsText(detail)}`] : []),
    'END:VEVENT', 'END:VCALENDAR', '',
  ].join('\r\n');
}
